import { CharComp } from '@/components/CharComp'
import { CollectCoin } from '@/components/CollectCoin'
import '@/layouts/kline-panel-layout.scss'
import classNames from 'classnames'
import { useI18n } from 'i18n-utils'
import { useKlineStore, useQuoteStore, useStaticTableDataStore, useUserInfoStore } from 'store'
import { ArrowDownSolidIcon } from 'ui-components'
import ResponsiveTrading from './ResponsiveTrading.tsx'

interface KlinePanelProps {}

export const KlinePanel = defineComponent<KlinePanelProps>({
  name: 'KlinePanel',
  setup() {
    const quoteStore = useQuoteStore()
    const klineStore = useKlineStore()
    const { t } = useI18n()
    const staticTableDataStore = useStaticTableDataStore()
    const userInfoStore = useUserInfoStore()

    const coinName = computed(() => {
      if (!klineStore.currentSymbolId) return ''
      return quoteStore.getSpotSymbolName(klineStore.currentSymbolId)
    })

    const currentKline = computed(() => klineStore.currentSpotKline)

    const currentSpotQuote = computed(() => {
      if (!klineStore.currentSymbolId) return null
      return quoteStore.allSpotList.find((item) => item.symbolId === klineStore.currentSymbolId)
    })

    const currencyList = computed(() => staticTableDataStore.currencyList)

    const mark = computed(() => currencyList.value.find((item) => item.logicName === userInfoStore.currentToCurrency)?.mark || '￥')

    const coinIcon = computed(() => {
      const url = quoteStore.getCoinIconBySymbolId(klineStore.currentSymbolId!)
      if (!url) return ''
      return url
    })

    return () => (
      <div class="kline-panel-layout">
        <div class="coin-info">
          <div class="collect-coin trade-kine-icon">{klineStore.currentSymbolId && <CollectCoin symbolId={klineStore.currentSymbolId} />}</div>
          <ResponsiveTrading>
            <div class="flex items-center coin-name">
              <img class={'coin-icon'} src={coinIcon.value} alt="" />
              <CharComp>{coinName.value}</CharComp>
              <ArrowDownSolidIcon class="ml-7" />
            </div>
          </ResponsiveTrading>
          <div class="current-price">
            <div
              class={classNames('usdt', {
                up: klineStore.klineDirection === 'up',
                down: klineStore.klineDirection === 'down',
              })}
            >
              <CharComp>{currentKline.value?.close.formatNumberWithCommas().decimalPadZero(klineStore.coinToPrecision) || '--'}</CharComp>
            </div>

            <div class={'cny'}>
              <CharComp>
                ≈{mark.value}
                &nbsp;
                {quoteStore.getLastCurrencyPrice(klineStore.currentSymbolId!)}
              </CharComp>
            </div>
          </div>
        </div>
        <div class="price-change">
          <div class="price-change-item">
            <CharComp class={'label'}>{t('涨幅')}</CharComp>
            <CharComp
              class={classNames('value', {
                up: quoteStore.getLastPriceChange(klineStore.currentSymbolId!).isGreaterThan(0),
                down: quoteStore.getLastPriceChange(klineStore.currentSymbolId!).isLessThan(0),
              })}
            >
              {quoteStore.getLastPriceChange(klineStore.currentSymbolId!).isGreaterThan(0) ? '+' : ''}
              {quoteStore.getLastPriceChange(klineStore.currentSymbolId!).formatNumberWithCommas()}%
            </CharComp>
          </div>
          <div class="price-change-item">
            <CharComp class={'label'}>{t('24H高')}</CharComp>
            <CharComp class={'value'}>{currentSpotQuote.value?.high.formatNumberWithCommas().decimalPadZero(klineStore.coinToPrecision)}</CharComp>
          </div>
          <div class="price-change-item">
            <CharComp class={'label'}>{t('24H低')}</CharComp>
            <CharComp class={'value'}>{currentSpotQuote.value?.low.formatNumberWithCommas().decimalPadZero(klineStore.coinToPrecision)}</CharComp>
          </div>
          <div class="price-change-item">
            <CharComp class={'label'}>{t('24H量')}</CharComp>
            <CharComp class={'value'}>{currentSpotQuote.value?.vol.formatNumberWithCommas().decimalPadZero(klineStore.coinFromPrecision).removeTrailingZero}</CharComp>
          </div>
        </div>
      </div>
    )
  },
})
